import React, { useMemo, useState } from 'react';
import { Message, PublicUser } from './api';
import { Icon } from './icons';

/** Forum-mode board for a channel: root messages are rendered as titled discussion topics
 *  (newest activity first) instead of a chat stream. Clicking a topic opens its thread.
 *  "New topic" posts a root message with a title. Replies live in the existing thread panel. */

function relTime(iso?: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric' });
}
function initials(s: string): string {
  const p = (s || '?').trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '?';
}

export function ForumBoard({ messages, meId, members, channelName, canPost, onOpenTopic, onNewTopic }: {
  messages: Message[];
  meId?: string;
  members: PublicUser[];
  channelName: string;
  canPost: boolean;
  onOpenTopic: (rootId: string) => void;
  onNewTopic: (title: string, body: string) => Promise<void>;
}) {
  const [composing, setComposing] = useState(false);

  // Topics = root messages, most-recently-active first.
  const topics = useMemo(() => {
    const roots = messages.filter((m) => !m.thread_root_id);
    return [...roots].sort((a, b) => {
      const ta = Date.parse(a.last_reply_at || a.created_at || '') || 0;
      const tb = Date.parse(b.last_reply_at || b.created_at || '') || 0;
      return tb - ta;
    });
  }, [messages]);

  const nameFor = (m: Message) => m.sender_name || members.find((x) => x.id === m.sender_id)?.name || 'Someone';

  return (
    <div className="fx-forum">
      <div className="fx-forum-head">
        <div>
          <div className="fx-forum-title">Topics</div>
          <div className="fx-forum-sub">{topics.length} {topics.length === 1 ? 'topic' : 'topics'} in #{channelName}</div>
        </div>
        {canPost && (
          <button type="button" className="fx-forum-new" onClick={() => setComposing(true)}>
            <Icon name="plus" size={15} /> New topic
          </button>
        )}
      </div>

      <div className="fx-forum-list">
        {topics.length === 0 ? (
          <div className="fx-forum-empty">
            <div className="fx-forum-empty-ic"><Icon name="dm" size={30} /></div>
            <div className="fx-forum-empty-t">No topics yet</div>
            <div className="fx-forum-empty-s">Start a discussion — each topic gets its own threaded conversation, so nothing gets lost in the stream.</div>
            {canPost && <button type="button" className="fx-forum-new" onClick={() => setComposing(true)}><Icon name="plus" size={15} /> New topic</button>}
          </div>
        ) : topics.map((m) => {
          const title = (m.title || '').trim() || (m.body || '').split('\n')[0].slice(0, 120) || 'Untitled topic';
          const rawPreview = m.title ? (m.body || '') : (m.body || '').split('\n').slice(1).join(' ');
          const preview = rawPreview.trim() === title ? '' : rawPreview;
          const replies = m.reply_count || 0;
          return (
            <button type="button" key={m.id} className="fx-forum-card" onClick={() => onOpenTopic(m.id)}>
              <span className="fx-forum-avatar" style={m.sender_avatar ? { backgroundImage: `url(${m.sender_avatar})` } : undefined}>
                {!m.sender_avatar && initials(nameFor(m))}
              </span>
              <span className="fx-forum-body">
                <span className="fx-forum-cardtitle">{title}</span>
                {preview.trim() && <span className="fx-forum-preview">{preview.trim().slice(0, 160)}</span>}
                <span className="fx-forum-meta">
                  <span>{nameFor(m)}{m.sender_id === meId ? ' (you)' : ''}</span>
                  <span>·</span>
                  <span>{relTime(m.last_reply_at || m.created_at)}</span>
                </span>
              </span>
              <span className={`fx-forum-replies${replies ? ' has' : ''}`}>
                <Icon name="dm" size={13} /> {replies}
              </span>
            </button>
          );
        })}
      </div>

      {composing && <NewTopicModal channelName={channelName} onClose={() => setComposing(false)} onSubmit={onNewTopic} />}
    </div>
  );
}

function NewTopicModal({ channelName, onClose, onSubmit }: {
  channelName: string; onClose: () => void; onSubmit: (title: string, body: string) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (busy || !title.trim()) return;
    setErr(null); setBusy(true);
    try { await onSubmit(title.trim(), body.trim()); onClose(); }
    catch (e: any) { setErr(e?.message || 'Could not post the topic.'); setBusy(false); }
  };

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 94vw)' }}>
        <div className="fx-login-title">New topic in #{channelName}</div>
        <input autoFocus className="fx-text-input" style={{ width: '100%', boxSizing: 'border-box', marginTop: 12, fontSize: 15, fontWeight: 600 }}
          value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Topic title"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget.nextElementSibling as HTMLTextAreaElement)?.focus(); } }} />
        <textarea className="fx-text-input" style={{ width: '100%', boxSizing: 'border-box', marginTop: 10, minHeight: 140, resize: 'vertical' }}
          value={body} onChange={(e) => setBody(e.target.value)} placeholder="What would you like to discuss?" />
        {err && <div style={{ color: 'var(--fx-alert,#e56a6a)', fontSize: 13, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="fx-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="fx-sendbtn" disabled={busy || !title.trim()} onClick={submit}>
            {busy ? 'Posting…' : 'Post topic'}
          </button>
        </div>
      </div>
    </div>
  );
}
